'use client';

import { useMemo, useState, type JSX } from 'react';

import { ParentSize } from '@visx/responsive';

import KalshiLogo from 'components/DesignSystem/Icons/adhoc/KalshiLogo';
import { useFitScale } from 'hooks/useFitScale';
import { useKpowHistory, useKpowSnapshot } from 'queries/kpow';
import type { KpowDisplayParty, KpowHistoryPoint } from 'react_pages/AmericanPowerIndex/api/kpowApi';

import { KPOW_BLUE, KPOW_RED } from '../kpowColors';
import { FLAT_THRESHOLD, formatDelta, formatScrubDate, pointAtAge, pointDate, WEEK_MS } from '../kpowFormat';
import BroadcastChart from './BroadcastChart';

/**
 * Locked content canvas (the 1280px inner layout of Figma 761:3680). The design is
 * never reflowed; it's contain-scaled to whatever container the host gives it. The
 * host owns framing: the broadcast page centers it in a 16:9 box with white margin,
 * the embed widget lets it fill the iframe.
 */
const STAGE_W = 1280;
const STAGE_H = 720;

const leanLabel = (party: KpowDisplayParty): string => {
    if (party === 'R') return 'Republican Lean';
    if (party === 'D') return 'Democrat Lean';
    return 'Even';
};

const partyColor = (party: KpowDisplayParty): string => {
    if (party === 'R') return KPOW_RED;
    if (party === 'D') return KPOW_BLUE;
    return 'var(--text-x10)';
};

/** Direction color for the 1W delta: red toward R, blue toward D, neutral when flat. */
const changeColor = (change: number): string => {
    if (change > FLAT_THRESHOLD) return KPOW_RED;
    if (change < -FLAT_THRESHOLD) return KPOW_BLUE;
    return 'var(--text-x10)';
};

type MonthAnchor = 'start' | 'middle' | 'end';

interface MonthTick {
    key: string;
    label: string;
    /** Position as a percent of the chart's time span, so it lines up with the x-axis. */
    leftPct: number;
    anchor: MonthAnchor;
}

/** Edge ticks anchor inward so they don't spill past the chart; interior ticks center on their mark. */
const monthAnchor = (leftPct: number): MonthAnchor => {
    if (leftPct < 4) return 'start';
    if (leftPct > 96) return 'end';
    return 'middle';
};

const monthTranslate = (anchor: MonthAnchor): string => {
    if (anchor === 'start') return 'none';
    if (anchor === 'end') return 'translateX(-100%)';
    return 'translateX(-50%)';
};

/**
 * Month-start ticks placed at their true fraction of the data's time span (skipping the partial
 * leading month), so each label lines up with where that month actually starts on the chart rather
 * than being evenly distributed.
 */
const monthTicks = (start: Date, end: Date): MonthTick[] => {
    const startMs = start.getTime();
    const span = end.getTime() - startMs;
    if (span <= 0) return [];
    const ticks: MonthTick[] = [];
    let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    if (cursor.getTime() < startMs) cursor = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    while (cursor.getTime() <= end.getTime()) {
        const leftPct = ((cursor.getTime() - startMs) / span) * 100;
        ticks.push({
            key: `${cursor.getFullYear()}-${cursor.getMonth()}`,
            label: cursor.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
            leftPct,
            anchor: monthAnchor(leftPct),
        });
        cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
    return ticks;
};

/**
 * Standalone broadcast frame for the American Power Index: a fixed 16:9 canvas with
 * the KPOW headline value + 1W delta and a red/blue line chart on a fixed +-10 window.
 * Auto-refreshes via the shared KPOW queries. Scrubbing the chart dims the future side
 * and drives the headline to the hovered reading + date.
 */
const BroadcastKpow = (): JSX.Element => {
    const [stageRef, scale] = useFitScale(STAGE_W, STAGE_H);
    const snapshotQuery = useKpowSnapshot();
    const historyQuery = useKpowHistory();
    const snapshot = snapshotQuery.data ?? null;

    // Append the live snapshot as the rightmost point so the line + headline agree
    // (mirrors AmericanPowerIndexContent / KpowChartWidget).
    const points = useMemo<KpowHistoryPoint[]>(() => {
        const historyPoints = historyQuery.data?.points ?? [];
        if (!snapshot) return historyPoints;
        const lastTs = historyPoints[historyPoints.length - 1]?.end_period_ts ?? 0;
        if (snapshot.data_as_of <= lastTs) return historyPoints;
        return [
            ...historyPoints,
            {
                end_period_ts: snapshot.data_as_of,
                signed_value: snapshot.composite.signed_value,
                display_party: snapshot.composite.display_party,
                display_value: snapshot.composite.display_value,
            },
        ];
    }, [historyQuery.data, snapshot]);

    const composite = snapshot?.composite ?? null;
    // While scrubbing the chart, the headline reflects the hovered reading + its date instead of
    // the live value + 1W delta.
    const [hoverPoint, setHoverPoint] = useState<KpowHistoryPoint | null>(null);
    const headline = hoverPoint ?? composite;
    const valueText = headline ? `+${headline.display_value.toFixed(2)}` : '';
    const weekChange = useMemo(() => {
        if (points.length === 0) return 0;
        const weekAgo = pointAtAge(points, WEEK_MS);
        return weekAgo ? points[points.length - 1].signed_value - weekAgo.signed_value : 0;
    }, [points]);

    // Kept behind useMemo (rather than left to React Compiler) because this component is also
    // rendered by the standalone widget app, whose next.config.js has no `reactCompiler` setting —
    // without this, every scrub-frame setHoverPoint update would rebuild every monthly Date and
    // re-run toLocaleDateString formatting even though `points` didn't change.
    const months = useMemo(
        () =>
            points.length === 0
                ? []
                : monthTicks(pointDate(points[0].end_period_ts), pointDate(points[points.length - 1].end_period_ts)),
        [points],
    );

    return (
        <div ref={stageRef} className="absolute inset-0 overflow-hidden bg-core-white">
            <div
                className="absolute left-1/2 top-1/2 flex flex-col box-border px-[44px] py-[32px]"
                style={{
                    width: STAGE_W,
                    height: STAGE_H,
                    transform: `translate(-50%, -50%) scale(${scale})`,
                    transformOrigin: 'center',
                    visibility: scale ? 'visible' : 'hidden',
                }}
            >
                {/* Brand + headline */}
                <div className="flex flex-col gap-[32px] pb-[28px]">
                    <div className="flex items-center gap-[8px]">
                        <div className="flex flex-1 min-w-0 items-center gap-[16px]">
                            <div className="flex h-[56px] w-[142px] shrink-0 items-center justify-center rounded-full bg-core-black">
                                <span className="font-kalshi-sans font-semibold text-[32px] leading-[56px] tracking-[0.32px] text-core-white">
                                    KPOW
                                </span>
                            </div>
                            <span className="font-kalshi-sans text-[32px] leading-[56px] tracking-[-1.6px] text-text-x10 whitespace-nowrap">
                                American Power Index
                            </span>
                        </div>
                        <KalshiLogo color="var(--text-x10)" height="37px" />
                    </div>
                    <div className="flex items-baseline gap-[16px] whitespace-nowrap">
                        <span
                            className="font-kalshi-sans font-medium text-[60px] leading-[56px] tracking-[-3px] tabular-nums"
                            style={{ color: headline ? partyColor(headline.display_party) : 'var(--text-x10)' }}
                        >
                            {valueText}
                        </span>
                        <span className="font-kalshi-sans font-medium text-[60px] leading-[56px] tracking-[-3px] text-text-x10">
                            {headline ? leanLabel(headline.display_party) : ''}
                        </span>
                        {hoverPoint ? (
                            <span className="font-kalshi-sans font-medium text-[32px] leading-[26px] text-text-x20">
                                {formatScrubDate(hoverPoint.end_period_ts)}
                            </span>
                        ) : null}
                        {!hoverPoint && points.length > 0 ? (
                            <span
                                className="font-kalshi-sans font-medium text-[32px] leading-[26px] tabular-nums"
                                style={{ color: changeColor(weekChange) }}
                            >
                                {`${formatDelta(weekChange)} 1W`}
                            </span>
                        ) : null}
                    </div>
                </div>

                {/* Chart + axis + caption */}
                <div className="flex flex-1 min-h-0 flex-col gap-[24px]">
                    <div className="flex-1 min-h-0">
                        <ParentSize debounceTime={0}>
                            {({ width, height }): JSX.Element => (
                                <BroadcastChart
                                    width={width}
                                    height={height}
                                    points={points}
                                    onHoverPoint={setHoverPoint}
                                />
                            )}
                        </ParentSize>
                    </div>
                    <div className="relative h-[19px] font-kalshi-sans text-[19px] leading-[16px] text-text-x10 tabular-nums">
                        {months.map((tick) => (
                            <span
                                key={tick.key}
                                className="absolute top-0 whitespace-nowrap"
                                style={{ left: `${tick.leftPct}%`, transform: monthTranslate(tick.anchor) }}
                            >
                                {tick.label}
                            </span>
                        ))}
                    </div>
                    <span className="font-kalshi-sans italic text-[29px] leading-[36px] tracking-[-0.87px] text-text-x30">
                        +10 Represents total control
                    </span>
                </div>
            </div>
        </div>
    );
};

export default BroadcastKpow;
